import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
} from '@mui/material';
import { Avatar } from '@components/primitives/Avatar';
import { Patient } from '../../../types/types';
import SlideSidePane from '../../../Views/Shared/SlideSidePane';
import PatientInformationView from '../PatientInformationView';
import { PatientCommunicationPreferences } from '../PatientCommunicationPreferences';
import BaseBlock from './BaseBlock';
import { parseISO, differenceInYears } from 'date-fns';
import { Edit } from '@mui/icons-material';
import { useAccessToken, useSubjectLanguage } from '@features/user';
import { capitalize } from '../../../utils/utils';
import { formatPatientName } from '@utils/patientUtils';
import { Link } from 'react-router-dom';
import APIService from '../../../services/APIService';
import { EligibilityWarningIcon } from '@features/eligibility-checks';
import { useSnackbar } from 'notistack';
import { patientValidationSchema, patientFieldValidators } from '../patientValidation';
import { useModal } from '@store/hooks';
import { MODAL_TYPES } from '../../modals/constants';

interface PatientOverviewBlockProps {
  patient: Patient;
  onPatientUpdate?: (updatedPatient: Patient) => void;
  actions?: React.ReactNode;
  mode?: 'dense' | 'default';
  linkToPatient?: boolean;
}

const PatientOverviewBlock: React.FC<PatientOverviewBlockProps> = ({
  patient,
  onPatientUpdate,
  actions,
  mode = 'default',
  linkToPatient = false,
}) => {
  const [showEditPane, setShowEditPane] = useState(false);
  const [tempDob, setTempDob] = useState(patient.dob || '');
  const [tempGender, setTempGender] = useState(patient.gender || '');
  const [tempPronouns, setTempPronouns] = useState(patient.pronouns || '');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { getAccessToken } = useAccessToken();
  const subjectLanguage = useSubjectLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const { open: openModal } = useModal();

  // Handler to open insurance modal
  const handleAddInsurance = () => {
    openModal(MODAL_TYPES.INSURANCE_EDIT, {
      patientId: patient.patient_id,
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = parseISO(dateString);
    return date.toLocaleDateString();
  };

  const calculateAge = (dateString: string) => {
    if (!dateString) return null;
    try {
      const birthDate = parseISO(dateString);
      const age = differenceInYears(new Date(), birthDate);
      return age;
    } catch {
      return null;
    }
  };

  const formatDateWithAge = (dateString: string) => {
    if (!dateString) return 'N/A';
    const formattedDate = formatDate(dateString);
    const age = calculateAge(dateString);
    return age !== null ? `${formattedDate} (${age}y)` : formattedDate;
  };

  const getDefaultPronouns = (gender: string) => {
    switch (gender?.toLowerCase()) {
      case 'male':
        return 'he/him';
      case 'female':
        return 'she/her';
      case 'non-binary':
      case 'nonbinary':
        return 'they/them';
      default:
        return '';
    }
  };

  const getMissingFields = () => {
    const missing = [];
    if (!patient.dob) missing.push('dob');
    if (!patient.gender) missing.push('gender');
    return missing;
  };

  const hasMissingFields = getMissingFields().length > 0;

  // Update temp values when patient changes
  React.useEffect(() => {
    setTempDob(patient.dob || '');
    setTempGender(patient.gender || '');
    setTempPronouns(patient.pronouns || '');
  }, [patient]);

  const saveField = async (field: string, value: any) => {
    if (isSaving) return;

    // Validate the field before saving
    const editedPatient = { ...patient, [field]: value };
    const result = patientValidationSchema.safeParse(editedPatient);

    if (!result.success) {
      const fieldErrors: { [key: string]: string } = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);

      // Show error notification
      const errorMessage = fieldErrors[field] || 'Validation failed';
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
      return;
    }

    // Clear errors if validation passed
    setErrors({});

    try {
      setIsSaving(true);
      const accessToken = await getAccessToken();
      const updateData = { [field]: value };
      const updatedPatient = { ...patient, ...updateData };

      const response = await APIService.makeAPIPostRequest({
        requestString: '/patient/update',
        accessToken,
        body: {
          patient_id: patient.patient_id,
          updated_fields: updateData,
        },
      });

      if (response.ok) {
        if (onPatientUpdate) {
          onPatientUpdate(updatedPatient);
        }
        // Silent save, no notification for auto-save
      } else {
        throw new Error('Failed to update patient');
      }
    } catch (error) {
      console.error('Error updating patient:', error);
      enqueueSnackbar(`Failed to save ${field}`, {
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveFields = async (fields: Record<string, any>) => {
    if (isSaving) return;

    // Validate the fields before saving
    const editedPatient = { ...patient, ...fields };
    const result = patientValidationSchema.safeParse(editedPatient);

    if (!result.success) {
      const fieldErrors: { [key: string]: string } = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);

      // Show error notification for first error
      const firstErrorField = Object.keys(fieldErrors)[0];
      const errorMessage = fieldErrors[firstErrorField] || 'Validation failed';
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
      return;
    }

    // Clear errors if validation passed
    setErrors({});

    try {
      setIsSaving(true);
      const accessToken = await getAccessToken();
      const updatedPatient = { ...patient, ...fields };

      const response = await APIService.makeAPIPostRequest({
        requestString: '/patient/update',
        accessToken,
        body: {
          patient_id: patient.patient_id,
          updated_fields: fields,
        },
      });

      if (response.ok) {
        if (onPatientUpdate) {
          onPatientUpdate(updatedPatient);
        }
        // Silent save, no notification for auto-save
      } else {
        throw new Error('Failed to update patient');
      }
    } catch (error) {
      console.error('Error updating patient:', error);
      const fieldNames = Object.keys(fields).join(', ');
      enqueueSnackbar(`Failed to save ${fieldNames}`, {
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDobBlur = () => {
    if (tempDob && tempDob !== patient.dob) {
      saveField('dob', tempDob);
    }
  };

  const handleGenderBlur = () => {
    if (tempGender && tempGender !== patient.gender) {
      saveField('gender', tempGender);
    }
  };

  const handlePronounsBlur = () => {
    if (tempPronouns !== patient.pronouns) {
      saveField('pronouns', tempPronouns);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent, field: string) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      switch (field) {
        case 'dob':
          handleDobBlur();
          break;
        case 'gender':
          handleGenderBlur();
          break;
        case 'pronouns':
          handlePronounsBlur();
          break;
      }
    }
  };

  const handlePatientSave = (updatedPatient: Patient) => {
    if (onPatientUpdate) {
      onPatientUpdate(updatedPatient);
    }
  };

  const renderFieldValue = (fieldName: string, value: string) => {
    if (!value) {
      return (
        <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          Not provided
        </Typography>
      );
    }

    if (fieldName === 'dob') {
      return <Typography variant="body2">{formatDateWithAge(value)}</Typography>;
    }

    if (fieldName === 'gender') {
      return (
        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
          {value}
        </Typography>
      );
    }

    return <Typography variant="body2">{value}</Typography>;
  };

  const renderInlineFields = () => {
    if (!hasMissingFields) return null;

    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {!patient.dob && (
            <Grid item xs={12} sm={!patient.gender ? 6 : 12}>
              <TextField
                label="Date of Birth"
                type="date"
                size="small"
                fullWidth
                value={tempDob}
                onChange={e => setTempDob(e.target.value)}
                onBlur={handleDobBlur}
                onKeyPress={e => handleKeyPress(e, 'dob')}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().split('T')[0],
                }}
                disabled={isSaving}
                error={!!errors.dob}
                helperText={errors.dob}
              />
            </Grid>
          )}

          {!patient.gender && (
            <Grid item xs={12} sm={!patient.dob ? 6 : 12}>
              <FormControl size="small" fullWidth disabled={isSaving}>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={tempGender}
                  onChange={e => {
                    const newGender = e.target.value;
                    setTempGender(newGender);

                    if (newGender && newGender !== 'other') {
                      // Auto-set default pronouns for standard genders
                      const defaultPronouns = getDefaultPronouns(newGender);
                      setTempPronouns(defaultPronouns);
                      // Save both gender and pronouns in a single API call
                      saveFields({
                        gender: newGender,
                        pronouns: defaultPronouns,
                      });
                    } else if (newGender === 'other') {
                      // Clear pronouns for "other" so user can specify
                      setTempPronouns('');
                      saveField('gender', newGender);
                    }
                  }}
                  onBlur={handleGenderBlur}
                  label="Gender"
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Only show pronouns field if gender is "other" */}
          {tempGender === 'other' && (
            <Grid item xs={12}>
              <TextField
                label="Please specify pronouns"
                size="small"
                fullWidth
                value={tempPronouns}
                onChange={e => setTempPronouns(e.target.value)}
                onBlur={handlePronounsBlur}
                onKeyPress={e => handleKeyPress(e, 'pronouns')}
                placeholder="e.g., he/him, she/her, they/them, ze/zir"
                disabled={isSaving}
                required
                helperText="Please specify pronouns for 'Other' gender"
              />
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Press Enter to save
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => setShowEditPane(true)}
            sx={{ textTransform: 'none' }}
          >
            Complete all demographic info →
          </Button>
        </Box>
      </Box>
    );
  };

  const renderContent = () => {
    if (mode === 'dense') {
      return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {linkToPatient ? (
            <Link
              to={`/patient/${patient.patient_id}`}
              style={{ textDecoration: 'none', alignSelf: 'flex-start' }}
            >
              <Avatar
                id={patient.patient_id}
                firstName={patient.first_name}
                lastName={patient.last_name}
                sx={{
                  width: 40,
                  height: 40,
                  cursor: 'pointer',
                }}
              />
            </Link>
          ) : (
            <Avatar
              id={patient.patient_id}
              firstName={patient.first_name}
              lastName={patient.last_name}
              sx={{
                width: 40,
                height: 40,
              }}
            />
          )}

          <Box sx={{ flexGrow: 1 }}>
            {linkToPatient ? (
              <Link
                to={`/patient/${patient.patient_id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  {formatPatientName(patient.first_name, patient.last_name, patient.preferred_name)}
                </Typography>
              </Link>
            ) : (
              <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 500 }}>
                {formatPatientName(patient.first_name, patient.last_name, patient.preferred_name)}
              </Typography>
            )}

            <Grid container spacing={1} sx={{ color: 'text.secondary' }}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                  <span style={{ color: 'text.secondary', opacity: 0.7 }}>ID:</span>
                  {patient.identifier || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <span
                    style={{
                      color: 'text.secondary',
                      opacity: 0.7,
                      fontSize: '0.875rem',
                    }}
                  >
                    DOB:
                  </span>
                  {renderFieldValue('dob', patient.dob || '')}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <span
                    style={{
                      color: 'text.secondary',
                      opacity: 0.7,
                      fontSize: '0.875rem',
                    }}
                  >
                    Gender:
                  </span>
                  {renderFieldValue('gender', patient.gender || '')}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <span
                    style={{
                      color: 'text.secondary',
                      opacity: 0.7,
                      fontSize: '0.875rem',
                    }}
                  >
                    Pronouns:
                  </span>
                  {patient.pronouns ? (
                    renderFieldValue('pronouns', patient.pronouns)
                  ) : patient.gender ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {getDefaultPronouns(patient.gender)}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                    >
                      Not provided
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Collapse in={hasMissingFields} timeout={400}>
              {renderInlineFields()}
            </Collapse>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {linkToPatient ? (
          <Link to={`/patient/${patient.patient_id}`} style={{ textDecoration: 'none' }}>
            <Avatar
              id={patient.patient_id}
              firstName={patient.first_name}
              lastName={patient.last_name}
              sx={{
                width: 80,
                height: 80,
                cursor: 'pointer',
              }}
            />
          </Link>
        ) : (
          <Avatar
            id={patient.patient_id}
            firstName={patient.first_name}
            lastName={patient.last_name}
            sx={{
              width: 80,
              height: 80,
            }}
          />
        )}

        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {linkToPatient ? (
              <Link
                to={`/patient/${patient.patient_id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                  gutterBottom
                  sx={{ cursor: 'pointer', textDecoration: 'underline', mb: 0 }}
                >
                  {formatPatientName(patient.first_name, patient.last_name, patient.preferred_name)}
                </Typography>
              </Link>
            ) : (
              <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 0 }}>
                {formatPatientName(patient.first_name, patient.last_name, patient.preferred_name)}
              </Typography>
            )}
            <EligibilityWarningIcon 
              patientId={patient.patient_id} 
              source="patient_overview" 
              onAddInsurance={handleAddInsurance}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Identifier (PRN)
              </Typography>
              <Typography variant="body1">{patient.identifier || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Date of Birth
              </Typography>
              {renderFieldValue('dob', patient.dob || '')}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Gender
              </Typography>
              {renderFieldValue('gender', patient.gender || '')}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Pronouns
                {!patient.pronouns && patient.gender && (
                  <Typography variant="caption" sx={{ ml: 1, fontStyle: 'italic', opacity: 0.7 }}>
                    (defaults to {getDefaultPronouns(patient.gender)})
                  </Typography>
                )}
              </Typography>
              {patient.pronouns ? (
                renderFieldValue('pronouns', patient.pronouns)
              ) : patient.gender ? (
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  {getDefaultPronouns(patient.gender)}
                </Typography>
              ) : (
                <Typography variant="body1" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                  Not provided
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Phone Number
              </Typography>
              <Typography variant="body1">{patient.phone || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">{patient.email || 'N/A'}</Typography>
            </Grid>
          </Grid>

          {renderInlineFields()}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <BaseBlock
        title={`${capitalize(subjectLanguage)} Overview`}
        actions={
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <PatientCommunicationPreferences
              patientId={patient.patient_id}
              mobilePhone={patient.mobile_phone || patient.phone}
              email={patient.email}
            />
            <Box
              sx={{
                width: '1px',
                height: '1rem',
                bgcolor: 'divider',
              }}
            />
            <Tooltip title="Edit information" arrow>
              <Edit
                onClick={() => setShowEditPane(true)}
                sx={{
                  fontSize: 16,
                  color: 'text.secondary',
                  opacity: 0.7,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              />
            </Tooltip>
            {actions && (
              <>
                <Box
                  sx={{
                    width: '1px',
                    height: '1rem',
                    bgcolor: 'divider',
                  }}
                />
                {actions}
              </>
            )}
          </Box>
        }
      >
        {renderContent()}
      </BaseBlock>

      <SlideSidePane
        open={showEditPane}
        onClose={() => setShowEditPane(false)}
        title={`Edit ${capitalize(subjectLanguage)} Information`}
      >
        <PatientInformationView patientId={patient.patient_id} onPatientSave={handlePatientSave} />
      </SlideSidePane>
    </>
  );
};

export default PatientOverviewBlock;
